<template>
  <v-menu
    v-model="expanded"
    :close-on-content-click="false"
    location="bottom end"
    offset="4"
    transition="scale-transition"
  >
    <template #activator="{ props: activatorProps }">
      <v-btn
        v-bind="activatorProps"
        class="module-switcher-toggle"
        color="#ebecec"
        size="35"
        variant="text"
        icon
      >
        <v-icon icon="mdi-apps" />
      </v-btn>
    </template>
    <v-card
      class="module-switcher-menu"
      elevation="8"
    >
      <div
        v-if="regularModules.length"
        class="module-switcher-grid"
      >
        <button
          v-for="module in regularModules"
          :key="module.name"
          class="module-switcher-item"
          @click="navigateTo(module)"
        >
          <v-icon
            v-if="isMdiIcon(module.icon)"
            :icon="module.icon"
            class="module-switcher-item-icon"
            size="28"
          />
          <img
            v-else
            :src="module.icon"
            :alt="module.label"
            class="module-switcher-item-icon-img"
          />
          <span class="module-switcher-item-label">{{ module.label }}</span>
        </button>
      </div>

      <hr
        v-if="regularModules.length && adminModules.length"
        class="module-switcher-divider"
      />

      <div
        v-if="adminModules.length"
        class="module-switcher-grid"
      >
        <button
          v-for="module in adminModules"
          :key="module.name"
          class="module-switcher-item"
          @click="navigateTo(module)"
        >
          <v-icon
            v-if="isMdiIcon(module.icon)"
            :icon="module.icon"
            class="module-switcher-item-icon"
            size="28"
          />
          <img
            v-else
            :src="module.icon"
            :alt="module.label"
            class="module-switcher-item-icon-img"
          />
          <span class="module-switcher-item-label">{{ module.label }}</span>
        </button>
      </div>
    </v-card>
  </v-menu>
</template>

<script>
import { mapGetters } from 'vuex';
import safetyIcon from '@/assets/images/infusion-safety-icon-blue.svg';
import formularyIcon from '@/assets/images/formulary-icon-blue.svg';

import {
  ANALYTICS_CAPABILITY,
  SSO_CONFIG_CAPABILITY,
  USER_CONFIG_CAPABILITY,
  DEBUG_ADMIN_CAPABILITY,
} from '@/lib/identity-methods';

// Tenants the IV Formulation Management module is enabled for. Kept in sync
// with LandingPage's list for the matching card.
const FORMULARY_TENANTS = ['demo', 'demop'];

export default {
  name: 'ModuleSwitcher',
  props: {
    modules: {
      type: Array,
      default: () => [
        {
          name: 'safety',
          label: 'Safety',
          icon: safetyIcon,
          route: '/safety',
          capabilities: [ANALYTICS_CAPABILITY],
        },
        {
          name: 'formulary',
          label: 'IV Formulation Management',
          icon: formularyIcon,
          route: '/ivformulation',
          // Gated on tenant rather than capability, matching the landing page's
          // rule for the same card. Routing to /ivformulation also inherits that
          // route's beforeEnter, so the demo tenant lands on the separately
          // served DS Utilization app exactly as it does from the landing page.
          tenants: FORMULARY_TENANTS,
        },
        {
          name: 'user-admin',
          label: 'User Admin',
          icon: 'mdi-shield-account',
          route: '/user-admin',
          capabilities: [USER_CONFIG_CAPABILITY, SSO_CONFIG_CAPABILITY],
          isAdmin: true,
        },
        {
          name: 'debug',
          label: 'Debug',
          icon: 'mdi-bug',
          route: '/debug',
          capabilities: [DEBUG_ADMIN_CAPABILITY],
          isAdmin: true,
        },
      ],
    },
  },
  data() {
    return { expanded: false };
  },
  computed: {
    ...mapGetters(['userHasCapability', 'tenant']),
    visibleModules() {
      return this.modules.filter((module) => {
        // Tenant-gated modules are hidden outside their tenants, so the switcher
        // never offers a route the tenant can't enter.
        if (module.tenants && !module.tenants.includes(this.tenant?.name?.toLowerCase())) return false;
        // No capabilities defined = visible to everyone who can see the switcher
        if (!module.capabilities || module.capabilities.length === 0) return true;
        // Match the router's rule: user needs AT LEAST ONE capability from the list
        return module.capabilities.some((cap) => this.userHasCapability(cap));
      });
    },
    regularModules() {
      return this.visibleModules.filter((m) => !m.isAdmin);
    },
    adminModules() {
      return this.visibleModules.filter((m) => m.isAdmin);
    },
  },
  methods: {
    isMdiIcon(iconSrc) {
      return typeof iconSrc === 'string' && iconSrc.startsWith('mdi-');
    },
    navigateTo(module) {
      this.expanded = false;
      if (this.$route.path !== module.route) {
        this.$router.push({ path: module.route });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/sass/main.scss';

.module-switcher-menu {
  width: 320px;
  padding: 16px;
  border-radius: 8px;
  display: block; // override v-card's default flex
}

.module-switcher-header {
  font-size: 14px;
  font-weight: 600;
  color: $bainbridge-blue;
  padding-bottom: 8px;
  margin-bottom: 8px;
  border-bottom: 1px solid #eee;
}

.module-switcher-divider {
  border: none;
  border-top: 1px solid #eee;
  margin: 12px 0;
}

.module-switcher-grid {
  display: grid !important;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  width: 100%;
}

.module-switcher-item {
  display: flex !important;
  flex-direction: column !important;
  align-items: center;
  // Top-aligned, not centered: labels range from one line ("Safety") to three
  // ("IV Formulation Management" at this tile width). Centering the icon+label
  // group meant a taller label pushed its icon upward, so icons across a row
  // no longer lined up.
  justify-content: flex-start;
  padding: 12px 8px;
  border-radius: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: background-color 0.15s;
  width: 100%;

  &:hover {
    background-color: $bainbridge-gray-light;
  }
}

.module-switcher-item-icon {
  color: $bainbridge-blue !important;
  margin-bottom: 6px;
}

.module-switcher-item-label {
  font-size: 12px;
  color: $bainbridge-blue;
  text-align: center;
  line-height: 1.2;
  display: block;
  width: 100%;
  // Reserve the tallest label's height (3 lines x 1.2 line-height at 12px) so
  // every tile in the grid is the same height and the row bottoms align.
  min-height: 43px;
}

.module-switcher-item-icon-img {
  width: 28px;
  height: 28px;
  object-fit: contain;
  margin-bottom: 6px;
}
</style>
